import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { byCostCenterCode, costCenterLabel, costCenterMatches } from '@loot/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { changed, FormModal, FormSection, ReasonDialog } from '../../components/FormModal.tsx';
import {
  ListToolbar,
  matchesStatus,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { ConfirmDialog, Modal } from '../../components/Modal.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  PageHeader,
  Panel,
  Select,
  Table,
  Textarea,
  ValueTable,
} from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { PartialPayrollNotice, type PayrollView } from './Payroll.tsx';

interface Option {
  id: string;
  code: string;
  name: string;
}

/** Nome curto do mês no idioma da pessoa ("jan.", "Jan"). */
export function useMonthLabel() {
  const { i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { month: 'short', timeZone: 'UTC' });
  return (month: string) => fmt.format(new Date(`${month.slice(0, 7)}-01T00:00:00Z`));
}

/** Mês e ano no idioma da pessoa ("jan. de 2026", "Jan 2026"), de "2026-01" ou "2026-01-01". */
export function useMonthYear() {
  const { i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return (month: string | null | undefined) =>
    month ? fmt.format(new Date(`${month.slice(0, 7)}-01T00:00:00Z`)) : '—';
}

/** Número decimal no idioma da pessoa, com as mesmas casas com que veio ("0.0500" → "0,0500"). */
export function useDecimal() {
  const { i18n } = useTranslation();
  return (value: string) => {
    const places = value.split('.')[1]?.length ?? 0;
    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    }).format(value as unknown as number);
  };
}

/** Algum dos textos contém a busca (sem diferença entre maiúsculas e minúsculas)? */
export function matchesSearch(search: string, ...values: (string | null | undefined)[]) {
  const needle = search.trim().toLowerCase();
  return !needle || values.some((v) => v?.toLowerCase().includes(needle));
}

/**
 * Sem exercício no Calendário Orçamentário não dá para criar versão nem importar o realizado: diz
 * o porquê e leva até lá (guia: desabilitado sempre explica por perto).
 */
export function NoFiscalYearNotice({ id }: { id?: string }) {
  const { t } = useTranslation();
  return (
    <p id={id} className="text-sm text-muted">
      {t('fpaPlan.noYear.text')}{' '}
      <Link to="/fpa/settings/calendar" className="text-brand-text hover:underline">
        {t('fpaPlan.noYear.link')}
      </Link>
    </p>
  );
}

/** Situação do mês no calendário: só "Aberto" é positivo; "Em fechamento" está em andamento. */
const PERIOD_TONE = { future: 'neutral', open: 'ok', closing: 'info', closed: 'neutral' } as const;

// ---------------------------------------------------------------------------
// Calendário orçamentário
// ---------------------------------------------------------------------------

interface Calendar {
  years: { id: string; year: number; status: string; notes: string | null }[];
  periods: {
    id: string;
    fiscalYearId: string;
    legalEntityId: string;
    month: string;
    status: keyof typeof PERIOD_TONE;
    statusReason: string | null;
    closedAt: string | null;
    closedByName: string | null;
  }[];
  entities: Option[];
  actions: { manage: boolean; close: boolean };
}

/** Calendário orçamentário (ADR 0032, Fase 1B): exercícios e meses por empresa. */
export function FpaCalendarPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const monthLabel = useMonthLabel();
  const monthYear = useMonthYear();
  const fmt = useFormat();
  const [newYear, setNewYear] = useState<string | null>(null);
  const [opening, setOpening] = useState<{ id: string; year: number; initial: string[] } | null>(
    null,
  );
  const [chosen, setChosen] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [step, setStep] = useState<'view' | 'close' | 'reopen'>('view');
  const data = useQuery({
    queryKey: ['fpa', 'calendar'],
    queryFn: () => api.get<Calendar>('/fpa/calendar'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa'] });
  const createYear = useMutation({
    mutationFn: (year: string) => api.post('/fpa/fiscal-years', { year: Number(year) }),
    onSuccess: async () => {
      setNewYear(null);
      await refresh();
    },
  });
  const createPeriods = useMutation({
    mutationFn: ({ id, legalEntityIds }: { id: string; legalEntityIds: string[] }) =>
      api.post(`/fpa/fiscal-years/${id}/periods`, { legalEntityIds }),
    onSuccess: async () => {
      setOpening(null);
      await refresh();
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ status, reason }: { status: string; reason?: string }) =>
      api.post(`/fpa/periods/${selected}/status`, { status, reason: reason || null }),
    onSuccess: async () => {
      setStep('view');
      await refresh();
    },
  });

  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const { years, periods, actions } = data.data;
  const entityCode = new Map(data.data.entities.map((e) => [e.id, e.code]));
  const entityName = new Map(data.data.entities.map((e) => [e.id, e.name]));
  const period = periods.find((p) => p.id === selected);
  const nextYear = String(
    years.length ? Math.max(...years.map((y) => y.year)) + 1 : new Date().getUTCFullYear(),
  );
  const periodTitle = period
    ? t('fpaPlan.calendar.monthTitle', {
        entity: entityCode.get(period.legalEntityId) ?? '—',
        month: monthYear(period.month),
      })
    : '';
  const closeSelected = () => {
    setSelected(null);
    setStep('view');
    setStatus.reset();
  };
  const transitions =
    period && period.status !== 'closed' && actions.manage
      ? (['future', 'open', 'closing'] as const).filter((s) => s !== period.status)
      : [];
  const canClose = !!period && period.status !== 'closed' && actions.close;
  const canReopen = !!period && period.status === 'closed' && actions.close;

  return (
    <>
      <PageHeader
        title={t('nav.fpaCalendar')}
        description={t('fpa.screens.calendar')}
        actions={
          actions.manage ? (
            <Button
              onClick={() => {
                createYear.reset();
                setNewYear(nextYear);
              }}
            >
              {t('fpaPlan.calendar.newYear')}
            </Button>
          ) : null
        }
      />

      <FormModal
        open={newYear !== null}
        size="md"
        title={t('fpaPlan.calendar.newYear')}
        dirty={newYear !== null && newYear !== nextYear}
        saving={createYear.isPending}
        error={createYear.error}
        saveLabel={t('fpaPlan.calendar.createYear')}
        onSave={() => newYear && createYear.mutate(newYear)}
        onClose={() => setNewYear(null)}
      >
        <FormSection columns={1}>
          <Field
            label={t('fpaPlan.calendar.year')}
            hint={t('fpaPlan.calendar.yearHint')}
            error={issueFor(createYear.error, 'year')}
            required
          >
            <Input
              required
              type="number"
              min={2000}
              max={2100}
              value={newYear ?? ''}
              onChange={(ev) => setNewYear(ev.target.value)}
            />
          </Field>
        </FormSection>
      </FormModal>

      <FormModal
        open={opening !== null}
        size="md"
        title={t('fpaPlan.calendar.openMonthsTitle', { year: opening?.year })}
        description={t('fpaPlan.calendar.openMonthsHint')}
        dirty={!!opening && changed(opening.initial, chosen)}
        saving={createPeriods.isPending}
        error={createPeriods.error}
        saveLabel={t('fpaPlan.calendar.createMonths')}
        onSave={() =>
          opening &&
          chosen.length > 0 &&
          createPeriods.mutate({ id: opening.id, legalEntityIds: chosen })
        }
        onClose={() => setOpening(null)}
      >
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="mb-1 font-medium">
            {t('fpaPlan.calendar.entities')}
            <span aria-hidden className="ml-0.5 text-danger">
              *
            </span>
          </legend>
          {data.data.entities.map((e) => {
            const has = periods.some(
              (p) => p.fiscalYearId === opening?.id && p.legalEntityId === e.id,
            );
            return (
              <label key={e.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={chosen.includes(e.id)}
                  onChange={(ev) =>
                    setChosen((c) =>
                      ev.target.checked ? [...c, e.id] : c.filter((x) => x !== e.id),
                    )
                  }
                />
                <span className="font-mono text-xs">{e.code}</span> {e.name}
                {has && (
                  <span className="text-xs text-muted">· {t('fpaPlan.calendar.hasMonths')}</span>
                )}
              </label>
            );
          })}
          {chosen.length === 0 && (
            <span className="text-xs text-muted">{t('fpaPlan.calendar.chooseEntities')}</span>
          )}
        </fieldset>
      </FormModal>

      {years.length === 0 && (
        <p className="text-sm text-muted">
          {t(actions.manage ? 'fpaPlan.calendar.emptyManage' : 'fpaPlan.calendar.empty')}
        </p>
      )}
      {years.length > 0 && <p className="text-xs text-muted">{t('fpaPlan.calendar.clickHint')}</p>}
      {years.map((y) => {
        const mine = periods.filter((p) => p.fiscalYearId === y.id);
        const rows = [...new Set(mine.map((p) => p.legalEntityId))];
        const months = Array.from(
          { length: 12 },
          (_, i) => `${y.year}-${String(i + 1).padStart(2, '0')}-01`,
        );
        return (
          <Panel
            key={y.id}
            title={`${t('fpaPlan.calendar.year')} ${y.year}`}
            action={
              <span className="flex gap-2">
                {actions.manage && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const initial = data.data.entities
                        .filter((e) => !rows.includes(e.id))
                        .map((e) => e.id);
                      createPeriods.reset();
                      setChosen(initial);
                      setOpening({ id: y.id, year: y.year, initial });
                    }}
                  >
                    {t('fpaPlan.calendar.openMonths')}
                  </Button>
                )}
                <MasterDeleteButton table="fpa.fiscal_year" id={y.id} compact />
              </span>
            }
          >
            <ValueTable
              label={t('fpaPlan.calendar.monthsOf', { year: y.year })}
              head={[t('fpaPlan.entity'), ...months.map(monthLabel)]}
            >
              {rows.length === 0 && (
                <EmptyRow colSpan={13}>
                  {t(
                    actions.manage
                      ? 'fpaPlan.calendar.noPeriodsManage'
                      : 'fpaPlan.calendar.noPeriods',
                  )}
                </EmptyRow>
              )}
              {rows.map((entityId) => (
                <tr key={entityId}>
                  <td className="font-mono text-xs" title={entityName.get(entityId)}>
                    {entityCode.get(entityId) ?? '—'}
                  </td>
                  {months.map((m) => {
                    const p = mine.find((x) => x.legalEntityId === entityId && x.month === m);
                    const status = p ? t(`fpaPlan.periodStatus.${p.status}`) : '';
                    return (
                      <td key={m}>
                        {p ? (
                          <button
                            type="button"
                            className="rounded-full"
                            aria-label={t('fpaPlan.calendar.monthCell', {
                              entity: entityCode.get(entityId) ?? '—',
                              month: monthYear(m),
                              status,
                            })}
                            onClick={() => {
                              setStatus.reset();
                              setStep('view');
                              setSelected(p.id);
                            }}
                          >
                            <Badge tone={PERIOD_TONE[p.status]}>{status}</Badge>
                          </button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </ValueTable>
          </Panel>
        );
      })}

      {period && (
        <Modal
          open={step === 'view'}
          size="md"
          title={periodTitle}
          description={entityName.get(period.legalEntityId)}
          onClose={closeSelected}
          footer={
            <>
              <Button variant="secondary" onClick={closeSelected}>
                {t('common.close')}
              </Button>
              {transitions.map((s) => (
                <Button
                  key={s}
                  variant="secondary"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ status: s })}
                >
                  {t(`fpaPlan.calendar.to.${s}`)}
                </Button>
              ))}
              {canClose && (
                <Button disabled={setStatus.isPending} onClick={() => setStep('close')}>
                  {t('fpaPlan.calendar.to.closed')}
                </Button>
              )}
              {canReopen && (
                <Button variant="danger" onClick={() => setStep('reopen')}>
                  {t('fpaPlan.calendar.reopenMonth')}
                </Button>
              )}
              <MasterDeleteButton table="fpa.period" id={period.id} onDeleted={closeSelected} />
            </>
          }
        >
          <ErrorBox error={setStatus.error} />
          <div className="flex flex-col gap-2 text-sm">
            <p>
              <Badge tone={PERIOD_TONE[period.status]}>
                {t(`fpaPlan.periodStatus.${period.status}`)}
              </Badge>
              {period.closedAt && (
                <span className="ml-2 text-muted">
                  {t('fpaPlan.calendar.closedBy', {
                    name: period.closedByName ?? '—',
                    when: fmt.dateTime(period.closedAt),
                  })}
                </span>
              )}
            </p>
            {period.status === 'closed' && (
              <p className="text-muted">{t('fpaPlan.calendar.closedNote')}</p>
            )}
            {period.statusReason && (
              <p className="text-xs text-muted">
                {t('fpaPlan.calendar.lastReason')}: {period.statusReason}
              </p>
            )}
            {transitions.length === 0 && !canClose && !canReopen && (
              <p className="text-xs text-muted">{t('fpaPlan.calendar.noActions')}</p>
            )}
          </div>
        </Modal>
      )}
      <ConfirmDialog
        open={!!period && step === 'close'}
        title={t('fpaPlan.calendar.confirmCloseTitle', { month: periodTitle })}
        description={t('fpaPlan.calendar.confirmCloseText')}
        confirmLabel={t('fpaPlan.calendar.to.closed')}
        busy={setStatus.isPending}
        error={setStatus.error}
        onConfirm={() => setStatus.mutate({ status: 'closed' })}
        onCancel={() => {
          setStatus.reset();
          setStep('view');
        }}
      />
      <ReasonDialog
        open={!!period && step === 'reopen'}
        title={t('fpaPlan.calendar.reopenTitle', { month: periodTitle })}
        description={t('fpaPlan.calendar.reopenText')}
        fieldLabel={t('fpaPlan.calendar.reopenReason')}
        confirmLabel={t('fpaPlan.calendar.reopenMonth')}
        tone="danger"
        pending={setStatus.isPending}
        error={setStatus.error}
        onConfirm={(reason) => setStatus.mutate({ status: 'open', reason })}
        onClose={() => {
          setStatus.reset();
          setStep('view');
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Cenários
// ---------------------------------------------------------------------------

interface Scenario {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
}
type ScenarioForm = Omit<Scenario, 'id' | 'isActive'>;

/** Cenários do planejamento (base, otimista, pessimista…). */
export function FpaScenariosPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [editing, setEditing] = useState<{
    id: string | null;
    initial: ScenarioForm;
    form: ScenarioForm;
  } | null>(null);
  const [deactivating, setDeactivating] = useState<Scenario | null>(null);
  const list = useQuery({
    queryKey: ['fpa', 'scenarios'],
    queryFn: () => api.get<{ items: Scenario[]; actions: { manage: boolean } }>('/fpa/scenarios'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa'] });
  const save = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof editing>) =>
      id ? api.put(`/fpa/scenarios/${id}`, form) : api.post('/fpa/scenarios', form),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (s: Scenario) =>
      api.post(`/fpa/scenarios/${s.id}/status`, { isActive: !s.isActive }),
    onSuccess: async () => {
      setDeactivating(null);
      await refresh();
    },
  });
  const manage = list.data?.actions.manage;
  const f = editing?.form;
  const set = (patch: Partial<ScenarioForm>) =>
    setEditing((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e));
  const open = (s?: Scenario) => {
    save.reset();
    const initial = {
      code: s?.code ?? '',
      name: s?.name ?? '',
      description: s?.description ?? '',
    };
    setEditing({ id: s?.id ?? null, initial, form: initial });
  };
  const err = (path: string) => issueFor(save.error, path);
  const items = list.data?.items ?? [];
  const rows = items.filter(
    (s) => matchesStatus(s.isActive, status) && matchesSearch(q, s.code, s.name, s.description),
  );

  return (
    <>
      <PageHeader
        title={t('nav.fpaScenarios')}
        description={t('fpa.screens.scenarios')}
        actions={
          manage ? <Button onClick={() => open()}>{t('fpaPlan.scenarios.add')}</Button> : null
        }
      />
      <FormModal
        open={!!editing}
        size="md"
        title={t(editing?.id ? 'fpaPlan.scenarios.edit' : 'fpaPlan.scenarios.add')}
        dirty={!!editing && changed(editing.initial, editing.form)}
        saving={save.isPending}
        error={save.error}
        saveLabel={editing?.id ? undefined : t('fpaPlan.scenarios.create')}
        onSave={() => editing && save.mutate(editing)}
        onClose={() => setEditing(null)}
      >
        {f && (
          <FormSection columns={1}>
            <Field
              label={t('fields.code')}
              hint={t('fpaPlan.scenarios.codeHint')}
              error={err('code')}
              required
            >
              <Input
                required
                maxLength={30}
                className="font-mono"
                value={f.code}
                onChange={(ev) => set({ code: ev.target.value.toUpperCase() })}
              />
            </Field>
            <Field label={t('fields.name')} error={err('name')} required>
              <Input
                required
                maxLength={200}
                value={f.name}
                onChange={(ev) => set({ name: ev.target.value })}
              />
            </Field>
            <Field
              label={t('structure.fields.description')}
              hint={t('governance.optional')}
              error={err('description')}
            >
              <Textarea
                maxLength={1000}
                value={f.description ?? ''}
                onChange={(ev) => set({ description: ev.target.value })}
              />
            </Field>
          </FormSection>
        )}
      </FormModal>
      <ConfirmDialog
        open={!!deactivating}
        title={t('fpaPlan.scenarios.deactivateTitle', { name: deactivating?.name })}
        description={t('fpaPlan.scenarios.deactivateText')}
        confirmLabel={t('structure.deactivate')}
        tone="danger"
        busy={toggle.isPending}
        error={toggle.error}
        onConfirm={() => deactivating && toggle.mutate(deactivating)}
        onCancel={() => setDeactivating(null)}
      />
      {!deactivating && <ErrorBox error={toggle.error} />}
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <>
          <ListToolbar>
            <SearchBox value={q} onChange={setQ} placeholder={t('fpaPlan.scenarios.search')} />
            <StatusFilter value={status} onChange={setStatus} />
          </ListToolbar>
          <Table head={[t('fields.code'), t('fields.name'), t('fields.status'), '']}>
            {rows.length === 0 && (
              <EmptyRow colSpan={4}>
                {items.length === 0
                  ? t(manage ? 'fpaPlan.scenarios.emptyManage' : 'fpaPlan.scenarios.empty')
                  : t('fpaPlan.scenarios.noMatch')}
              </EmptyRow>
            )}
            {rows.map((s) => (
              <tr key={s.id}>
                <td className="font-mono text-xs">{s.code}</td>
                <td>
                  {s.name}
                  {s.description && <div className="text-xs text-muted">{s.description}</div>}
                </td>
                <td>
                  <Badge tone={s.isActive ? 'ok' : 'off'}>
                    {t(s.isActive ? 'fields.active' : 'fields.inactive')}
                  </Badge>
                </td>
                <td className="text-right whitespace-nowrap">
                  {manage && (
                    <span className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => open(s)}>
                        {t('common.edit')}
                      </Button>
                      {s.isActive ? (
                        <Button
                          variant="danger"
                          onClick={() => {
                            toggle.reset();
                            setDeactivating(s);
                          }}
                        >
                          {t('structure.deactivate')}
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          disabled={toggle.isPending}
                          onClick={() => toggle.mutate(s)}
                        >
                          {t('structure.activate')}
                        </Button>
                      )}
                      <MasterDeleteButton table="fpa.scenario" id={s.id} compact />
                    </span>
                  )}
                  {!manage && <MasterDeleteButton table="fpa.scenario" id={s.id} compact />}
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Premissas
// ---------------------------------------------------------------------------

interface AssumptionValue {
  id: string;
  validFrom: string;
  value: string;
  scenarioCode: string | null;
  entityCode: string | null;
  unitCode: string | null;
  replacesValueId: string | null;
  correctionReason: string | null;
  createdByName: string | null;
  createdAt: string;
  replaced: boolean;
}
interface Assumption {
  id: string;
  code: string;
  name: string;
  description: string | null;
  kind: 'value' | 'percent' | 'quantity' | 'index';
  unit: string | null;
  currency: string | null;
  isActive: boolean;
  isPayroll: boolean;
  values: AssumptionValue[];
}
type AssumptionOptions = {
  scenarios: Option[];
  entities: Option[];
  units: Option[];
  currencies: { code: string }[];
};
interface AssumptionList {
  items: Assumption[];
  options: AssumptionOptions | null;
  actions: { manage: boolean; addValues: boolean; payroll: boolean };
  payroll?: PayrollView;
}
type AssumptionForm = {
  code: string;
  name: string;
  description: string;
  kind: Assumption['kind'];
  unit: string;
  currency: string;
  isPayroll: boolean;
};
type ValueForm = {
  assumptionId: string;
  scenarioId: string;
  legalEntityId: string;
  businessUnitId: string;
  validFrom: string;
  value: string;
  replacesValueId: string | null;
  correctionReason: string;
};
const KINDS = ['value', 'percent', 'quantity', 'index'] as const;

/** Premissas financeiras com valores por vigência; valor gravado não muda (correção rastreável). */
export function FpaAssumptionsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [kind, setKind] = useState('');
  const [editing, setEditing] = useState<{
    id: string | null;
    initial: AssumptionForm;
    form: AssumptionForm;
  } | null>(null);
  const [valuesOf, setValuesOf] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<Assumption | null>(null);
  const list = useQuery({
    queryKey: ['fpa', 'assumptions'],
    queryFn: () => api.get<AssumptionList>('/fpa/assumptions'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa'] });
  const save = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof editing>) => {
      const body = {
        ...form,
        description: form.description || null,
        unit: form.unit || null,
        currency: form.kind === 'value' ? form.currency : null,
      };
      return id ? api.put(`/fpa/assumptions/${id}`, body) : api.post('/fpa/assumptions', body);
    },
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (a: Assumption) =>
      api.post(`/fpa/assumptions/${a.id}/status`, { isActive: !a.isActive }),
    onSuccess: async () => {
      setDeactivating(null);
      await refresh();
    },
  });
  const actions = list.data?.actions;
  const options = list.data?.options;
  const f = editing?.form;
  const set = (patch: Partial<AssumptionForm>) =>
    setEditing((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e));
  const open = (a?: Assumption) => {
    save.reset();
    const initial: AssumptionForm = {
      code: a?.code ?? '',
      name: a?.name ?? '',
      description: a?.description ?? '',
      kind: a?.kind ?? 'percent',
      unit: a?.unit ?? '',
      currency:
        a?.currency ?? (options?.currencies.length === 1 ? options.currencies[0]!.code : ''),
      isPayroll: a?.isPayroll ?? false,
    };
    setEditing({ id: a?.id ?? null, initial, form: initial });
  };
  const err = (path: string) => issueFor(save.error, path);
  const items = list.data?.items ?? [];
  const rows = items.filter(
    (a) =>
      matchesStatus(a.isActive, status) &&
      (!kind || a.kind === kind) &&
      matchesSearch(q, a.code, a.name, a.description),
  );
  const shown = items.find((a) => a.id === valuesOf);

  return (
    <>
      <PageHeader
        title={t('nav.fpaAssumptions')}
        description={t('fpa.screens.assumptions')}
        actions={
          actions?.manage ? (
            <Button onClick={() => open()}>{t('fpaPlan.assumptions.add')}</Button>
          ) : null
        }
      />
      <PartialPayrollNotice payroll={list.data?.payroll} />
      <FormModal
        open={!!editing}
        title={t(editing?.id ? 'fpaPlan.assumptions.edit' : 'fpaPlan.assumptions.add')}
        dirty={!!editing && changed(editing.initial, editing.form)}
        saving={save.isPending}
        error={save.error}
        saveLabel={editing?.id ? undefined : t('fpaPlan.assumptions.create')}
        onSave={() => editing && save.mutate(editing)}
        onClose={() => setEditing(null)}
      >
        {f && (
          <>
            <FormSection title={t('fpaPlan.assumptions.sectionIdentification')}>
              <Field
                label={t('fields.code')}
                hint={t('fpaPlan.assumptions.codeHint')}
                error={err('code')}
                required
              >
                <Input
                  required
                  maxLength={30}
                  className="font-mono"
                  value={f.code}
                  onChange={(ev) => set({ code: ev.target.value.toUpperCase() })}
                />
              </Field>
              <Field label={t('fields.name')} error={err('name')} required>
                <Input
                  required
                  maxLength={200}
                  value={f.name}
                  onChange={(ev) => set({ name: ev.target.value })}
                />
              </Field>
            </FormSection>
            <FormSection title={t('fpaPlan.assumptions.sectionMeasure')}>
              <Field label={t('fpaPlan.assumptions.kind')} error={err('kind')} required>
                <Select
                  required
                  value={f.kind}
                  onChange={(ev) => set({ kind: ev.target.value as AssumptionForm['kind'] })}
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`fpaPlan.assumptions.kinds.${k}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              {f.kind === 'value' ? (
                <Field label={t('fpaPlan.currency')} error={err('currency')} required>
                  <Select
                    required
                    value={f.currency}
                    onChange={(ev) => set({ currency: ev.target.value })}
                  >
                    <option value="">{t('common.choose')}</option>
                    {options?.currencies.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Field
                  label={t('fpaPlan.assumptions.unit')}
                  hint={t('fpaPlan.assumptions.unitHint')}
                  error={err('unit')}
                >
                  <Input
                    maxLength={30}
                    value={f.unit}
                    onChange={(ev) => set({ unit: ev.target.value })}
                  />
                </Field>
              )}
            </FormSection>
            <FormSection columns={1}>
              {/* Marcar protege; tirar a marca ou mudar premissa de pessoal exige a permissão. */}
              <Field label={t('fpaPayroll.assumption')} hint={t('fpaPayroll.assumptionHint')}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="fpa-assumption-payroll"
                    type="checkbox"
                    checked={f.isPayroll}
                    disabled={editing?.initial.isPayroll && !actions?.payroll}
                    onChange={(ev) => set({ isPayroll: ev.target.checked })}
                  />
                  {t('fpaPayroll.assumptionLabel')}
                </label>
              </Field>
              <Field
                label={t('structure.fields.description')}
                hint={t('governance.optional')}
                error={err('description')}
              >
                <Textarea
                  maxLength={1000}
                  value={f.description}
                  onChange={(ev) => set({ description: ev.target.value })}
                />
              </Field>
            </FormSection>
          </>
        )}
      </FormModal>
      {shown && options && actions && (
        <AssumptionValuesModal
          assumption={shown}
          options={options}
          canAdd={actions.addValues && shown.isActive}
          onClose={() => setValuesOf(null)}
        />
      )}
      <ConfirmDialog
        open={!!deactivating}
        title={t('fpaPlan.assumptions.deactivateTitle', { name: deactivating?.name })}
        description={t('fpaPlan.assumptions.deactivateText')}
        confirmLabel={t('structure.deactivate')}
        tone="danger"
        busy={toggle.isPending}
        error={toggle.error}
        onConfirm={() => deactivating && toggle.mutate(deactivating)}
        onCancel={() => setDeactivating(null)}
      />
      {!deactivating && <ErrorBox error={toggle.error} />}
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <>
          <ListToolbar>
            <SearchBox value={q} onChange={setQ} placeholder={t('fpaPlan.assumptions.search')} />
            <StatusFilter value={status} onChange={setStatus} />
            <Select
              className="w-full sm:w-48"
              aria-label={t('fpaPlan.assumptions.kind')}
              value={kind}
              onChange={(ev) => setKind(ev.target.value)}
            >
              <option value="">{t('fpaPlan.assumptions.anyKind')}</option>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`fpaPlan.assumptions.kinds.${k}`)}
                </option>
              ))}
            </Select>
          </ListToolbar>
          <Table
            head={[
              t('fields.code'),
              t('fields.name'),
              t('fpaPlan.assumptions.kind'),
              t('fields.status'),
              '',
            ]}
          >
            {rows.length === 0 && (
              <EmptyRow colSpan={5}>
                {items.length === 0
                  ? t(
                      actions?.manage
                        ? 'fpaPlan.assumptions.emptyManage'
                        : 'fpaPlan.assumptions.empty',
                    )
                  : t('fpaPlan.assumptions.noMatch')}
              </EmptyRow>
            )}
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="font-mono text-xs">{a.code}</td>
                <td>
                  {a.name}
                  {a.isPayroll && (
                    <span className="ml-2">
                      <Badge tone="info">{t('fpaPayroll.badge')}</Badge>
                    </span>
                  )}
                  {a.description && <div className="text-xs text-muted">{a.description}</div>}
                </td>
                <td>
                  {t(`fpaPlan.assumptions.kinds.${a.kind}`)}
                  {(a.currency ?? a.unit) && (
                    <span className="text-xs text-muted"> ({a.currency ?? a.unit})</span>
                  )}
                </td>
                <td>
                  <Badge tone={a.isActive ? 'ok' : 'off'}>
                    {t(a.isActive ? 'fields.active' : 'fields.inactive')}
                  </Badge>
                </td>
                <td className="text-right whitespace-nowrap">
                  <span className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setValuesOf(a.id)}>
                      {t('fpaPlan.assumptions.values', { count: a.values.length })}
                    </Button>
                    {actions?.manage && (
                      <>
                        <Button variant="secondary" onClick={() => open(a)}>
                          {t('common.edit')}
                        </Button>
                        {a.isActive ? (
                          <Button
                            variant="danger"
                            onClick={() => {
                              toggle.reset();
                              setDeactivating(a);
                            }}
                          >
                            {t('structure.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(a)}
                          >
                            {t('structure.activate')}
                          </Button>
                        )}
                      </>
                    )}
                    <MasterDeleteButton table="fpa.assumption" id={a.id} compact />
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </>
  );
}

/**
 * Valores de uma premissa, em janela: o histórico por vigência e, na mesma janela, o formulário do
 * valor novo ou da correção (o valor gravado não muda; a correção cria outro, com o motivo).
 */
function AssumptionValuesModal({
  assumption: a,
  options,
  canAdd,
  onClose,
}: {
  assumption: Assumption;
  options: AssumptionOptions;
  canAdd: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fmt = useFormat();
  const monthYear = useMonthYear();
  const decimal = useDecimal();
  const [value, setValue] = useState<{ initial: ValueForm; form: ValueForm } | null>(null);
  const addValue = useMutation({
    mutationFn: (v: ValueForm) =>
      api.post('/fpa/assumption-values', {
        ...v,
        scenarioId: v.scenarioId || null,
        legalEntityId: v.legalEntityId || null,
        businessUnitId: v.businessUnitId || null,
        correctionReason: v.replacesValueId ? v.correctionReason : null,
      }),
    onSuccess: async () => {
      setValue(null);
      await qc.invalidateQueries({ queryKey: ['fpa'] });
    },
  });
  const blank: ValueForm = {
    assumptionId: a.id,
    scenarioId: '',
    legalEntityId: '',
    businessUnitId: '',
    validFrom: '',
    value: '',
    replacesValueId: null,
    correctionReason: '',
  };
  const start = (form: ValueForm) => {
    addValue.reset();
    setValue({ initial: form, form });
  };
  const suffix = a.currency ?? a.unit;

  if (value) {
    const v = value.form;
    const setV = (patch: Partial<ValueForm>) =>
      setValue((x) => (x ? { ...x, form: { ...x.form, ...patch } } : x));
    const correcting = !!v.replacesValueId;
    const err = (path: string) => issueFor(addValue.error, path);
    return (
      <FormModal
        open
        title={t(
          correcting ? 'fpaPlan.assumptions.correctTitle' : 'fpaPlan.assumptions.addValueTitle',
          { name: a.name },
        )}
        description={correcting ? t('fpaPlan.assumptions.correctHint') : undefined}
        dirty={changed(value.initial, value.form)}
        saving={addValue.isPending}
        error={addValue.error}
        saveLabel={t('fpaPlan.assumptions.createValue')}
        onSave={() => addValue.mutate(v)}
        onClose={() => setValue(null)}
      >
        <FormSection>
          <Field
            label={t('fpaPlan.assumptions.validFrom')}
            error={err('validFrom')}
            required={!correcting}
          >
            <Input
              required
              type="month"
              disabled={correcting}
              value={v.validFrom}
              onChange={(ev) => setV({ validFrom: ev.target.value })}
            />
          </Field>
          <Field
            label={
              suffix
                ? `${t('fpaPlan.assumptions.value')} (${suffix})`
                : t('fpaPlan.assumptions.value')
            }
            hint={t('fpaPlan.decimalHint')}
            error={err('value')}
            required
          >
            <Input
              required
              inputMode="decimal"
              className="text-right tabular-nums"
              value={v.value}
              onChange={(ev) => setV({ value: ev.target.value.replace(',', '.') })}
            />
          </Field>
        </FormSection>
        <FormSection title={t('fpaPlan.assumptions.sectionScope')} columns={3}>
          <Field label={t('fpaPlan.scenario')} hint={t('fpaPlan.allHint')}>
            <Select
              disabled={correcting}
              value={v.scenarioId}
              onChange={(ev) => setV({ scenarioId: ev.target.value })}
            >
              <option value="">{t('fpaPlan.all')}</option>
              {options.scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('fpaPlan.entity')} hint={t('fpaPlan.allHint')}>
            <Select
              disabled={correcting}
              value={v.legalEntityId}
              onChange={(ev) => setV({ legalEntityId: ev.target.value })}
            >
              <option value="">{t('fpaPlan.all')}</option>
              {options.entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code} · {e.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('nav.businessUnits')} hint={t('fpaPlan.allHint')}>
            <Select
              disabled={correcting}
              value={v.businessUnitId}
              onChange={(ev) => setV({ businessUnitId: ev.target.value })}
            >
              <option value="">{t('fpaPlan.all')}</option>
              {options.units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} · {u.name}
                </option>
              ))}
            </Select>
          </Field>
        </FormSection>
        {correcting && (
          <FormSection columns={1}>
            <Field label={t('fpaPlan.assumptions.reason')} error={err('correctionReason')} required>
              <Textarea
                required
                minLength={3}
                value={v.correctionReason}
                onChange={(ev) => setV({ correctionReason: ev.target.value })}
              />
            </Field>
          </FormSection>
        )}
      </FormModal>
    );
  }

  return (
    <Modal
      open
      size="xl"
      title={t('fpaPlan.assumptions.valuesTitle', { name: a.name })}
      description={t('fpaPlan.assumptions.valuesHint')}
      onClose={onClose}
      footer={
        <>
          {!a.isActive && (
            <span className="mr-auto self-center text-xs text-muted">
              {t('fpaPlan.assumptions.inactiveNoValues')}
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
          {canAdd && (
            <Button onClick={() => start(blank)}>{t('fpaPlan.assumptions.addValue')}</Button>
          )}
        </>
      }
    >
      <Table
        head={[
          t('fpaPlan.assumptions.validFrom'),
          { label: t('fpaPlan.assumptions.value'), align: 'right' },
          t('fpaPlan.scenario'),
          t('fpaPlan.entity'),
          t('fpaPlan.assumptions.recorded'),
          '',
        ]}
      >
        {a.values.length === 0 && (
          <EmptyRow colSpan={6}>{t('fpaPlan.assumptions.noValues')}</EmptyRow>
        )}
        {a.values.map((v) => (
          <tr key={v.id} className={v.replaced ? 'text-muted' : undefined}>
            <td className="whitespace-nowrap">
              <span className={v.replaced ? 'line-through' : undefined}>
                {monthYear(v.validFrom)}
              </span>
              {v.replaced && (
                <div>
                  <Badge>{t('fpaPlan.assumptions.replaced')}</Badge>
                </div>
              )}
            </td>
            <td className="text-right whitespace-nowrap tabular-nums">
              <span className={v.replaced ? 'line-through' : undefined}>{decimal(v.value)}</span>
              {suffix && <span className="text-xs text-muted"> {suffix}</span>}
            </td>
            <td>{v.scenarioCode ?? t('fpaPlan.all')}</td>
            <td>{v.entityCode ?? v.unitCode ?? t('fpaPlan.all')}</td>
            <td className="text-xs">
              {v.createdByName ?? '—'} · {fmt.dateTime(v.createdAt)}
              {v.correctionReason && (
                <div className="text-muted">
                  {t('fpaPlan.assumptions.reason')}: {v.correctionReason}
                </div>
              )}
            </td>
            <td className="text-right">
              <span className="flex justify-end gap-2">
                {canAdd && !v.replaced && (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      start({
                        ...blank,
                        validFrom: v.validFrom.slice(0, 7),
                        value: v.value,
                        replacesValueId: v.id,
                      })
                    }
                  >
                    {t('fpaPlan.assumptions.correct')}
                  </Button>
                )}
                <MasterDeleteButton table="fpa.assumption_value" id={v.id} compact />
              </span>
            </td>
          </tr>
        ))}
      </Table>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Acessos do FP&A
// ---------------------------------------------------------------------------

interface Scope {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  legalEntityId: string;
  entityCode: string;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  includeChildren: boolean;
  isActive: boolean;
  notes: string | null;
}
interface AccessList {
  items: Scope[];
  options: {
    people: { id: string; name: string; email: string }[];
    entities: Option[];
    costCenters: { id: string; legalEntityId: string; code: string; name: string }[];
  };
}
type ScopeForm = {
  userId: string;
  legalEntityId: string;
  costCenterId: string;
  includeChildren: boolean;
  notes: string;
};

/**
 * Acessos do FP&A: quais números cada pessoa vê (empresa inteira ou centro de custo, com os de
 * baixo). O perfil diz o que a pessoa faz; o acesso, de onde. Conferido também no banco.
 */
export function FpaAccessPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [entity, setEntity] = useState('');
  const [form, setForm] = useState<{ initial: ScopeForm; form: ScopeForm } | null>(null);
  // Toda mudança de acesso pede o motivo (auditoria de 27/10).
  const [changing, setChanging] = useState<{ scope: Scope; patch: Partial<Scope> } | null>(null);
  const list = useQuery({
    queryKey: ['fpa', 'access'],
    queryFn: () => api.get<AccessList>('/fpa/access'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fpa'] });
  const create = useMutation({
    mutationFn: (f: ScopeForm) =>
      api.post('/fpa/access', {
        ...f,
        costCenterId: f.costCenterId || null,
        notes: f.notes,
      }),
    onSuccess: async () => {
      setForm(null);
      await refresh();
    },
  });
  const update = useMutation({
    mutationFn: (c: { scope: Scope; patch: Partial<Scope>; notes: string }) =>
      api.put(`/fpa/access/${c.scope.id}`, {
        includeChildren: c.patch.includeChildren ?? c.scope.includeChildren,
        isActive: c.patch.isActive ?? c.scope.isActive,
        notes: c.notes,
      }),
    onSuccess: async () => {
      setChanging(null);
      await refresh();
    },
  });
  const options = list.data?.options;
  const f = form?.form;
  const set = (patch: Partial<ScopeForm>) =>
    setForm((x) => (x ? { ...x, form: { ...x.form, ...patch } } : x));
  const open = () => {
    if (!options) return;
    create.reset();
    const initial: ScopeForm = {
      userId: '',
      legalEntityId: options.entities.length === 1 ? options.entities[0]!.id : entity,
      costCenterId: '',
      includeChildren: true,
      notes: '',
    };
    setForm({ initial, form: initial });
  };
  const err = (path: string) => issueFor(create.error, path);
  const items = list.data?.items ?? [];
  const rows = items.filter(
    (s) =>
      matchesStatus(s.isActive, status) &&
      (!entity || s.legalEntityId === entity) &&
      (matchesSearch(q, s.userName, s.userEmail) ||
        (!!s.costCenterCode && costCenterMatches(q, s.costCenterCode, s.costCenterName))),
  );

  return (
    <>
      <PageHeader
        title={t('nav.fpaAccess')}
        description={t('fpaPlan.access.description')}
        actions={options ? <Button onClick={open}>{t('fpaPlan.access.add')}</Button> : null}
      />
      <p className="text-sm text-muted">{t('fpaPlan.access.managersNote')}</p>
      <FormModal
        open={!!form}
        title={t('fpaPlan.access.add')}
        dirty={!!form && changed(form.initial, form.form)}
        saving={create.isPending}
        error={create.error}
        saveLabel={t('fpaPlan.access.create')}
        onSave={() => f && create.mutate(f)}
        onClose={() => setForm(null)}
      >
        {f && options && (
          <>
            <FormSection title={t('fpaPlan.access.sectionWho')} columns={1}>
              <Field label={t('fpaPlan.access.person')} error={err('userId')} required>
                <Select
                  required
                  value={f.userId}
                  onChange={(ev) => set({ userId: ev.target.value })}
                >
                  <option value="">{t('common.choose')}</option>
                  {options.people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.email}
                    </option>
                  ))}
                </Select>
              </Field>
            </FormSection>
            <FormSection title={t('fpaPlan.access.sectionWhere')}>
              <Field label={t('fpaPlan.entity')} error={err('legalEntityId')} required>
                <Select
                  required
                  value={f.legalEntityId}
                  onChange={(ev) => set({ legalEntityId: ev.target.value, costCenterId: '' })}
                >
                  <option value="">{t('common.choose')}</option>
                  {options.entities.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code} · {e.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('fpaPlan.costCenter')}
                hint={t('fpaPlan.access.wholeEntityHint')}
                error={err('costCenterId')}
              >
                <Select
                  disabled={!f.legalEntityId}
                  value={f.costCenterId}
                  onChange={(ev) => set({ costCenterId: ev.target.value })}
                >
                  <option value="">{t('fpaPlan.access.wholeEntity')}</option>
                  {options.costCenters
                    .filter((c) => c.legalEntityId === f.legalEntityId)
                    .sort(byCostCenterCode)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {costCenterLabel(c.code, c.name)}
                      </option>
                    ))}
                </Select>
              </Field>
              {f.costCenterId && (
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={f.includeChildren}
                    onChange={(ev) => set({ includeChildren: ev.target.checked })}
                  />
                  {t('fpaPlan.access.includeChildren')}
                </label>
              )}
            </FormSection>
            <FormSection columns={1}>
              <Field label={t('fpaPlan.access.reason')} required error={err('notes')}>
                <Textarea
                  maxLength={1000}
                  value={f.notes}
                  onChange={(ev) => set({ notes: ev.target.value })}
                />
              </Field>
            </FormSection>
          </>
        )}
      </FormModal>
      <ReasonDialog
        open={!!changing}
        title={
          changing?.patch.isActive === false
            ? t('fpaPlan.access.deactivateTitle', { name: changing.scope.userName })
            : t('fpaPlan.access.changeTitle', { name: changing?.scope.userName })
        }
        description={
          changing?.patch.isActive === false
            ? t('fpaPlan.access.deactivateText')
            : t('fpaPlan.access.changeText')
        }
        fieldLabel={t('fpaPlan.access.reason')}
        confirmLabel={
          changing?.patch.isActive === false ? t('structure.deactivate') : t('common.save')
        }
        tone={changing?.patch.isActive === false ? 'danger' : 'primary'}
        pending={update.isPending}
        error={update.error}
        onConfirm={(notes) => changing && update.mutate({ ...changing, notes })}
        onClose={() => setChanging(null)}
      />
      {!changing && <ErrorBox error={update.error} />}
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <>
          <ListToolbar>
            <SearchBox value={q} onChange={setQ} placeholder={t('fpaPlan.access.search')} />
            <StatusFilter value={status} onChange={setStatus} />
            <Select
              className="w-full sm:w-56"
              aria-label={t('fpaPlan.entity')}
              value={entity}
              onChange={(ev) => setEntity(ev.target.value)}
            >
              <option value="">{t('common.allCompanies')}</option>
              {list.data.options.entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code} · {e.name}
                </option>
              ))}
            </Select>
          </ListToolbar>
          <Table
            head={[
              t('fpaPlan.access.person'),
              t('fpaPlan.entity'),
              t('fpaPlan.costCenter'),
              t('fpaPlan.access.children'),
              t('fields.status'),
              '',
            ]}
          >
            {rows.length === 0 && (
              <EmptyRow colSpan={6}>
                {items.length === 0 ? t('fpaPlan.access.empty') : t('fpaPlan.access.noMatch')}
              </EmptyRow>
            )}
            {rows.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.userName}
                  <div className="text-xs text-muted">{s.userEmail}</div>
                  {s.notes && <div className="text-xs text-muted">{s.notes}</div>}
                </td>
                <td className="font-mono text-xs">{s.entityCode}</td>
                <td>
                  {s.costCenterCode
                    ? costCenterLabel(s.costCenterCode, s.costCenterName)
                    : t('fpaPlan.access.wholeEntity')}
                </td>
                <td>
                  {s.costCenterId ? t(s.includeChildren ? 'fpaPlan.yes' : 'fpaPlan.no') : '—'}
                </td>
                <td>
                  <Badge tone={s.isActive ? 'ok' : 'off'}>
                    {t(s.isActive ? 'fields.active' : 'fields.inactive')}
                  </Badge>
                </td>
                <td className="text-right whitespace-nowrap">
                  <span className="flex justify-end gap-2">
                    {s.costCenterId && (
                      <Button
                        variant="secondary"
                        disabled={update.isPending}
                        onClick={() => {
                          update.reset();
                          setChanging({ scope: s, patch: { includeChildren: !s.includeChildren } });
                        }}
                      >
                        {t(
                          s.includeChildren
                            ? 'fpaPlan.access.onlyThis'
                            : 'fpaPlan.access.withChildren',
                        )}
                      </Button>
                    )}
                    {s.isActive ? (
                      <Button
                        variant="danger"
                        onClick={() => {
                          update.reset();
                          setChanging({ scope: s, patch: { isActive: false } });
                        }}
                      >
                        {t('structure.deactivate')}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        disabled={update.isPending}
                        onClick={() => {
                          update.reset();
                          setChanging({ scope: s, patch: { isActive: true } });
                        }}
                      >
                        {t('structure.activate')}
                      </Button>
                    )}
                    <MasterDeleteButton table="fpa.access_scope" id={s.id} compact />
                  </span>
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </>
  );
}
